import type { CmmdProjectTaskDraftProposal, CmmdProjectTaskDraftTask } from "@cmmd-forge/contracts";
import { useEffect, useState } from "react";

import { MarkdownEditor } from "~/components/rich-content/MarkdownEditor";
import { Input } from "~/components/ui/input";
import { NO_ZOOM_FIELD_TEXT_CLASS } from "~/lib/touchTarget";
import {
  normalizeAcceptanceCriteriaInput,
  MAX_DEPENDENCIES_PER_TASK,
  normalizeRequiredTaskText,
  updateTask,
  updateTaskDependencies,
} from "./taskDraftModel";

export interface ProjectLeadTaskInspectorProps {
  readonly proposal: CmmdProjectTaskDraftProposal;
  readonly task: CmmdProjectTaskDraftTask;
  readonly disabled: boolean;
  readonly onChange: (proposal: CmmdProjectTaskDraftProposal) => void;
}

export function ProjectLeadTaskInspector(props: ProjectLeadTaskInspectorProps) {
  const children = props.proposal.tasks.filter(
    (task) => task.parentTaskRef === props.task.localRef,
  );
  const update = (changes: Parameters<typeof updateTask>[2]) =>
    props.onChange(updateTask(props.proposal, props.task.localRef, changes));
  const atDependencyLimit = props.task.dependencyRefs.length >= MAX_DEPENDENCIES_PER_TASK;
  return (
    <section className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex min-h-5 items-center rounded-md border border-border bg-muted px-2 font-medium">
            {props.task.parentTaskRef === null ? "Parent task" : "Subtask"}
          </span>
          <span>{props.task.workItemType}</span>
          <span aria-hidden>·</span>
          <span>{props.task.priority} priority</span>
        </div>
        <RequiredTextInput
          key={`${props.task.localRef}:title`}
          label="Task title"
          value={props.task.title}
          disabled={props.disabled}
          onApply={(title) => update({ title })}
          maxLength={500}
          className="h-auto border-0 px-0 text-lg font-semibold shadow-none focus-visible:ring-0 md:text-xl"
        />
        <RequiredTextInput
          key={`${props.task.localRef}:goal`}
          label="Goal"
          value={props.task.goal}
          disabled={props.disabled}
          onApply={(goal) => update({ goal })}
          maxLength={2_000}
          placeholder="What outcome should this task achieve?"
        />
        <Field label="Description">
          <div
            aria-label="Task description editor"
            aria-disabled={props.disabled}
            className={props.disabled ? "pointer-events-none opacity-60" : undefined}
          >
            <RequiredDescriptionEditor
              key={`${props.task.localRef}:description`}
              value={props.task.description}
              disabled={props.disabled}
              onApply={(description) => update({ description })}
              ariaLabel="Task description"
              placeholder="Add implementation context…"
              maxLength={5_000}
            />
          </div>
        </Field>
        <AcceptanceCriteriaEditor
          key={props.task.localRef}
          criteria={props.task.acceptanceCriteria}
          disabled={props.disabled}
          onApply={(acceptanceCriteria) => update({ acceptanceCriteria })}
        />
        <FieldGroup label={`Dependencies (${props.task.dependencyRefs.length})`}>
          <div className="space-y-1 rounded-lg border border-border p-2">
            {props.proposal.tasks
              .filter((task) => task.localRef !== props.task.localRef)
              .map((task) => {
                const checked = props.task.dependencyRefs.includes(task.localRef);
                return (
                  <label
                    key={task.localRef}
                    className="flex min-h-touch items-center gap-3 rounded-md px-3 text-sm hover:bg-muted md:min-h-9 md:gap-2 md:px-2"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={props.disabled || (atDependencyLimit && !checked)}
                      onChange={(event) =>
                        props.onChange(
                          updateTaskDependencies(
                            props.proposal,
                            props.task.localRef,
                            task.localRef,
                            event.currentTarget.checked,
                          ),
                        )
                      }
                    />
                    <span className="truncate">{task.title}</span>
                  </label>
                );
              })}
            {atDependencyLimit ? (
              <p role="alert" className="px-2 py-1 text-xs text-warning-foreground">
                Tasks are limited to 20 dependencies. Remove one before adding another.
              </p>
            ) : null}
            {props.proposal.tasks.length === 1 ? (
              <p className="px-2 py-1 text-sm text-muted-foreground">
                Add another task to define dependencies.
              </p>
            ) : null}
          </div>
        </FieldGroup>
        <FieldGroup label={`Subtasks (${children.length})`}>
          <div className="rounded-lg border border-border">
            {children.map((child) => (
              <div
                key={child.localRef}
                className="border-b border-border px-3 py-2 text-sm last:border-b-0"
              >
                {child.title}
              </div>
            ))}
            {children.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">No subtasks yet.</p>
            ) : null}
          </div>
        </FieldGroup>
      </div>
    </section>
  );
}

function RequiredTextInput(props: {
  readonly label: string;
  readonly value: string;
  readonly disabled: boolean;
  readonly onApply: (value: string) => void;
  readonly placeholder?: string;
  readonly className?: string;
  readonly maxLength: number;
}) {
  const [text, setText] = useState(props.value);
  const [editing, setEditing] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  useEffect(() => {
    if (!editing) setText(props.value);
  }, [editing, props.value]);
  const apply = () => {
    const result = normalizeRequiredTaskText(text, props.value, props.maxLength, props.label);
    setText(result.value);
    setFeedback(result.feedback);
    props.onApply(result.value);
  };
  return (
    <Field label={props.label}>
      <Input
        value={text}
        disabled={props.disabled}
        onChange={(event) => {
          setText(event.currentTarget.value);
          setFeedback(null);
        }}
        onFocus={() => setEditing(true)}
        onBlur={() => {
          setEditing(false);
          apply();
        }}
        placeholder={props.placeholder}
        className={props.className}
      />
      {feedback !== null ? <FieldFeedback>{feedback}</FieldFeedback> : null}
    </Field>
  );
}

function RequiredDescriptionEditor(props: {
  readonly value: string;
  readonly disabled: boolean;
  readonly onApply: (value: string) => void;
  readonly ariaLabel: string;
  readonly placeholder: string;
  readonly maxLength: number;
}) {
  const [text, setText] = useState(props.value);
  const [editing, setEditing] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  useEffect(() => {
    if (!editing) setText(props.value);
  }, [editing, props.value]);
  return (
    <div
      onFocus={() => setEditing(true)}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        setEditing(false);
        const result = normalizeRequiredTaskText(text, props.value, props.maxLength, "Description");
        setText(result.value);
        setFeedback(result.feedback);
        props.onApply(result.value);
      }}
    >
      <MarkdownEditor
        disabled={props.disabled}
        value={text}
        onChange={(description) => {
          if (!props.disabled) {
            setText(description);
            setFeedback(null);
          }
        }}
        ariaLabel={props.ariaLabel}
        placeholder={props.placeholder}
        density="compact"
      />
      {feedback !== null ? <FieldFeedback>{feedback}</FieldFeedback> : null}
    </div>
  );
}

function AcceptanceCriteriaEditor(props: {
  readonly criteria: readonly string[];
  readonly disabled: boolean;
  readonly onApply: (criteria: string[]) => void;
}) {
  const [text, setText] = useState(() => props.criteria.join("\n"));
  const [editing, setEditing] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const externalText = props.criteria.join("\n");
  useEffect(() => {
    if (!editing) setText(externalText);
  }, [editing, externalText]);
  return (
    <Field label={`Acceptance criteria (${props.criteria.length})`}>
      <textarea
        value={text}
        disabled={props.disabled}
        onChange={(event) => {
          setText(event.currentTarget.value);
          setFeedback(null);
        }}
        onFocus={() => setEditing(true)}
        onBlur={() => {
          setEditing(false);
          const result = normalizeAcceptanceCriteriaInput(text);
          setText(result.criteria.join("\n"));
          setFeedback(result.feedback);
          props.onApply(result.criteria);
        }}
        aria-label="Acceptance criteria, one per line"
        placeholder="One verifiable criterion per line"
        className={`min-h-20 w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring ${NO_ZOOM_FIELD_TEXT_CLASS}`}
      />
      {feedback !== null ? <FieldFeedback>{feedback}</FieldFeedback> : null}
    </Field>
  );
}

function FieldFeedback(props: { readonly children: React.ReactNode }) {
  return (
    <p role="alert" className="text-xs text-warning-foreground">
      {props.children}
    </p>
  );
}

function Field(props: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {props.label}
      </span>
      {props.children}
    </label>
  );
}

function FieldGroup(props: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <fieldset className="block space-y-1.5">
      <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {props.label}
      </legend>
      {props.children}
    </fieldset>
  );
}
